import { API_ENDPOINT, WEBHOOK_ENDPOINT } from '@Env/variables';
import { Assigned, JiraAssigned, JiraMappings } from '@Models/types';
import { createAxiosInstance } from './axios';
import { isAxiosError } from 'axios';

export interface JiraMappingConflict {
  username: string;
  overlappingProjects: { id: string; key: string; name: string }[];
}

export interface JiraMappingConflictError {
  type: 'conflict';
  message: string;
  conflicts: JiraMappingConflict[];
}

const normalizeJiraMappings = (mappings: JiraMappings[]): JiraMappings[] => {
  return mappings.map((mapping) => ({
    ...mapping,
    projects: mapping.projects ?? [],
  }));
};

export const jiraAnalyzerReportsAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, WEBHOOK_ENDPOINT);

  return {
    getMappings: async (): Promise<JiraMappings[]> => {
      const { data } = await axios.get('/api/jira/analyzer/getMappings');
      return normalizeJiraMappings(data);
    },

    updateMappings: async (
      mappings: JiraMappings
    ): Promise<void | JiraMappingConflictError> => {
      try {
        await axios.post('/api/jira/analyzer/updateMappings', { ...mappings });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 409) {
          const body = err.response.data as {
            error: string;
            conflicts: JiraMappingConflict[];
          };
          return {
            type: 'conflict',
            message: body.error,
            conflicts: body.conflicts,
          };
        }
        throw err;
      }
    },

    deleteMapping: async (username: string): Promise<void> => {
      await axios.post('/api/jira/analyzer/deleteMapping', { username });
    },
  };
};

export const jiraAnalyzerAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, API_ENDPOINT);

  const jiraAnalyzer = {
    getAnalyzerIssues: async (
      key: string,
      maxResults: number,
      dateFilter: string,
      nextPageToken?: string
    ): Promise<{
      issues: JiraAssigned[];
      nextPageToken?: string;
      isLast: boolean;
    }> => {
      const { data } = await axios.post('/api/getAnalyzerIssues', {
        prefix: key,
        maxResults: maxResults,
        dateFilter,
        nextPageToken,
      });

      return {
        issues: (data.issues as JiraAssigned[]) ?? [],
        nextPageToken: data.nextPageToken,
        isLast: data.isLast ?? false,
      };
    },

    tagTickets: async (tickets: Assigned[]): Promise<Assigned[]> => {
      const { data } = await axios.post('/api/taggedJiraTickets', {
        tickets,
      });
      return data.tagged;
    },
  };

  return jiraAnalyzer;
};
